import React, { useEffect } from "react";
import { Text, Loader } from "@vibe/core";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import { formatDate } from "../../utils/unixDateFormatter";
import logger from "../../lib/logger";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";

interface ViewHistoryModalProps {
  columns: any;
  isOpen: boolean;
  onClose: () => void;
  columnType: string | null;
  columnTitle: string | null;
}

type LogType = {
  created_at: string;
  data: string;
  event: string;
  id: string;
};
const ViewHistoryModal = ({
  columns,
  isOpen,
  onClose,
  columnType,
  columnTitle,
}: ViewHistoryModalProps) => {
  const [history, setHistory] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  // Move columnLog inside useEffect or memoize it
  const columnLog = React.useMemo(() => {
    if (!columns || !columns.length) return [];

    return columns
      .map((item: any) => {
        const matchingColumn = item?.column_values?.find(
          (column: any) => column.type === columnType && column.column.title === columnTitle,
        );

        // Only return item if matching column exists
        if (!matchingColumn) return undefined;

        return {
          item_id: item?.item_id,
          board_id: item?.board?.id,
          board_name: item?.board?.name || "Unknown Board",
          column_type: columnType,
          column_id: matchingColumn.id,
          value: matchingColumn.value,
          text: matchingColumn.text || "No value",
          exists: true,
        };
      })
      .filter((item: any): item is NonNullable<typeof item> => item !== undefined);
  }, [columnType, columnTitle, columns]);

  useEffect(() => {
    const fetchUpdateLogs = async (columnLog: any) => {
      try {
        setLoading(true);
        const allLogs = await Promise.all(
          columnLog.map(async (column: any) => {
            const { item_id, column_id, board_id } = column;

            const response = await MondayApiQueryInstance.getColumnUpdateLog(
              item_id,
              column_id,
              board_id,
            );
            return response?.data?.boards[0];
          }),
        );

        setHistory(allLogs);
      } catch (error: any) {
        logger.error("Error fetching update logs:", error);
      } finally {
        setLoading(false);
      }
    };

    if (columnLog && columnLog.length > 0) {
      fetchUpdateLogs(columnLog);
    }
  }, [columnLog]);

  const getTextualValue = (logData: any) => {
    switch (logData.column_type) {
      case "color":
        return logData.value?.label?.text || "";
      // A CLEARED value arrives from Monday as `value: null` on every column type
      // (simb-pulse-ms guards the same shape for status/text/people/location), so no
      // branch below may dereference `value` directly — a throw here happens mid-render
      // and unmounts the whole modal rather than just blanking one cell. Absence is
      // returned as "" and rendered as an em-dash by the caller.
      case "date":
        return logData.value?.date || "";
      case "dropdown":
        const chosenValues = logData?.value?.chosenValues
          ?.map((value: any) => value.name)
          .join(", ");

        return chosenValues || "";
      case "tag":
        const tagValues = logData?.value?.tags?.map((tag: any) => tag.name).join(", ");
        return tagValues || "";

      case "timerange":
        // Interpolating a missing range printed a literal "undefined - undefined", which
        // the `|| ""` fallback could never catch because that string is truthy.
        const { from, to } = logData?.value ?? {};
        return from || to ? `${from ?? ""} - ${to ?? ""}` : "";
      default:
        return logData.textual_value || "";
    }
  };

  return (
    <Modal id="view-history" show={isOpen} onClose={onClose} size="medium">
      <ModalBasicLayout>
        <ModalHeader title="Activity Log" className="px-8" />
        <ModalContent>
          <div className="flex flex-col gap-4">
            {loading ? (
              <div className="flex justify-center items-center mx-auto">
                <Loader size="small" color="primary" />
              </div>
            ) : history && history.length > 0 ? (
              history.map((board: any, index: number) => (
                <div key={index} className="flex flex-col pb-2 rounded-md shadow-md bg-slate-100">
                  <div className="py-2 px-[6px] rounded-t-md font-bold text-base bg-primary-selected">
                    {board.name}
                  </div>

                  <div className="flex flex-col gap-1 px-2 py-2 mt-1 text-sm">
                    {board.activity_logs.map((log: LogType, logIndex: number) => {
                      const logData = JSON.parse(log.data);
                      const textualValue = getTextualValue(logData);

                      return (
                        <div className="grid grid-cols-[1fr_1fr] gap-2" key={logIndex}>
                          {/* An em-dash marks a value that was cleared, so the row does
                              not read as a rendering failure. */}
                          <div>{textualValue || "—"}</div>
                          <div>{formatDate(log.created_at) || "No date available"}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <Text align="inherit" element="p" type="text1">
                No history available.
              </Text>
            )}
          </div>
        </ModalContent>
        <ModalFooter
          primaryButton={{
            onClick: onClose,
            text: "Close",
          }}
        />
      </ModalBasicLayout>
    </Modal>
  );
};

export default ViewHistoryModal;
